import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { z } from 'zod';

import type { PublicEnquiryForm } from '~/modules/enquiry/types/public-enquiry-form';
import type { ConfiguredEnquiryAnswers } from '~/modules/enquiry/domain/configured-enquiry';
import {
    EMPTY_CONFIGURED_ANSWERS,
    buildConfiguredSelection,
    buildEnquiryFormPayload,
    buildFormContext,
    type ConfiguredEnquiryRules,
} from '~/modules/enquiry/domain/configured-enquiry';
import {
    DEFAULT_DECLINED_MESSAGE,
    readCreatedEnquiryId,
    readSubmitOutcome,
    type RoutedDestination,
} from '~/modules/enquiry/domain/enquiry-outcome';
import type { PredictedDestination } from '~/modules/enquiry/util/predict-enquiry-destination';
import { getMeetingRequestMessage } from '~/modules/enquiry/util/get-default-enquiry-message';
import {
    EMAIL_INPUT,
    FIRST_NAME_INPUT,
    LAST_NAME_INPUT,
    PHONE_NUMBER_INPUT,
} from '~/modules/validation/util/form';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import type { CalendarAvailabilityData } from '../api/get-calendar-availability.server';
import type { BookingRosterMember } from '../api/get-request-availability.server';
import { CTA_SOURCE_FIELD } from '../utils/cta-source';

dayjs.extend(utc);
dayjs.extend(timezone);

/** The most "What we cover" lines the gate shows; the rest read as a wall of text. */
const MAX_COVER_LINES = 4;

/** The location question's answer: the label typed, and the place picked for it. */
export interface BookingLocationAnswer {
    header: string;
    /** The place finder's place. `region` is what routing and the enquiry read. */
    value: { region?: string; addressID?: string } & Record<string, unknown>;
}

/**
 * The visitor's answers to a booking form. Plain data, so the gate can keep
 * them in sessionStorage across a sign-in that reloads the page.
 */
export interface BookingFormAnswers extends ConfiguredEnquiryAnswers {
    /** The chosen branch id as the select's value; `''` for none. */
    branchId: string;
    location: BookingLocationAnswer | null;
    /** Their own note, when the form has a message box. */
    message: string;
}

export const EMPTY_BOOKING_ANSWERS: BookingFormAnswers = {
    ...EMPTY_CONFIGURED_ANSWERS,
    branchId: '',
    location: null,
    message: '',
};

/** How the chosen time turns into a meeting. */
export type SlotSource = 'routed' | 'assigned' | 'member' | 'request';

/** The time the reader chose, frozen at the moment they confirmed it. */
export interface BookingSlot {
    /** The session the time was offered from; null for a preferred-time request. */
    sessionId: number | null;
    /** Why that session (or none) was offered. See {@link resolveMeetingTarget}. */
    source: SlotSource;
    /** The TeamMember row the meeting is with, when the reader chose one. */
    teamMemberId: number | null;
    /** The member account that hosts a booked slot. */
    organizerMemberId: number | null;
    startTimestamp: number;
    duration: number;
    timezoneName: string;
    /** Who the reader is meeting, as the gate named them. */
    subjectName: string;
    /** True when the practice rather than a person was named. */
    isCompany: boolean;
}

/** The "Your details" step's answers. */
export interface BookingContact {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    company: string;
}

export const EMPTY_BOOKING_CONTACT: BookingContact = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
};

/**
 * Whether a form asks anything the reader has to answer. Routing alone is not
 * a question: rules that only read the visitor's country or region are
 * resolved by the server from where they are.
 */
export function bookingFormAsksQuestions(form: PublicEnquiryForm): boolean {
    const branchChoices = form.branchPicker?.enabled
        ? form.branchPicker.options.length
        : 0;
    return (
        (form.enquiryTypesEnabled && form.enquiryTypes.length > 0) ||
        form.enquirerTypeEnabled ||
        form.projectKindQuestionEnabled ||
        form.locationEnabled ||
        form.fields.length > 0 ||
        branchChoices > 1
    );
}

/**
 * Whether the gate opens on the form's questions. A CTA that says the form
 * goes details-first also earns a step for the message box alone.
 */
export function shouldAskBookingDetails(
    form: PublicEnquiryForm | null,
    { detailsFirst }: { detailsFirst?: boolean } = {},
): boolean {
    if (!form) return false;
    if (bookingFormAsksQuestions(form)) return true;
    return detailsFirst === true && form.messageEnabled;
}

/** The meeting session a routing prediction lands on, if any. */
export const predictedSessionId = (
    predicted: PredictedDestination | null,
): number | null =>
    predicted?.destination === 'Calendar' ? predicted.calendarSessionId : null;

/** Where the chosen time is booked, or that it is asked for instead. */
export type MeetingTarget =
    | {
          mode: 'session';
          sessionId: number;
          source: Exclude<SlotSource, 'request'>;
          teamMemberId: number | null;
      }
    | { mode: 'request'; teamMemberId: number | null };

/**
 * The session a meeting is made against, in priority order:
 *
 * 1. the session the form's routing lands on (predicted before submit, the
 *    server's answer after) — it decides, whoever was picked;
 * 2. the person the reader chose (or the CTA named): their own connected
 *    calendar, else a request addressed to them;
 * 3. "anyone": the page's assigned session (a project's link, the practice's
 *    Client Director link);
 * 4. a request of the practice, over the server's configured working week.
 */
export function resolveMeetingTarget({
    routedSessionId,
    selectedMember,
    assignedSessionId,
}: {
    routedSessionId: number | null;
    selectedMember: Pick<
        BookingRosterMember,
        'teamMemberId' | 'sessionId'
    > | null;
    assignedSessionId: number | null;
}): MeetingTarget {
    if (routedSessionId) {
        return {
            mode: 'session',
            sessionId: routedSessionId,
            source: 'routed',
            teamMemberId: selectedMember?.teamMemberId ?? null,
        };
    }
    if (selectedMember) {
        return selectedMember.sessionId
            ? {
                  mode: 'session',
                  sessionId: selectedMember.sessionId,
                  source: 'member',
                  teamMemberId: selectedMember.teamMemberId,
              }
            : { mode: 'request', teamMemberId: selectedMember.teamMemberId };
    }
    if (assignedSessionId) {
        return {
            mode: 'session',
            sessionId: assignedSessionId,
            source: 'assigned',
            teamMemberId: null,
        };
    }
    return { mode: 'request', teamMemberId: null };
}

/** What happens once the enquiry exists and the server has said where it went. */
export type MeetingFollowUp =
    | { kind: 'book'; sessionId: number }
    | { kind: 'revalidate'; sessionId: number }
    | { kind: 'request' };

/**
 * Follows the server's routing, which is authoritative:
 *
 * - routed to a meeting session: book it when the chosen time came from that
 *   session, otherwise check the time against it first;
 * - routed anywhere else (a teammate, the inbox, a branch): a time offered
 *   only because the client predicted a meeting session becomes a request, so
 *   the enquiry's recipient confirms it; a time from a person's own calendar or
 *   the page's own session is still booked there;
 * - not reported: the chosen time stands as chosen.
 */
export function resolveMeetingFollowUp({
    routed,
    slot,
}: {
    routed: RoutedDestination | null;
    slot: Pick<BookingSlot, 'sessionId' | 'source'>;
}): MeetingFollowUp {
    const routedSession =
        routed?.kind === 'Calendar' ? routed.calendarSessionId : null;
    if (routedSession) {
        return slot.sessionId === routedSession
            ? { kind: 'book', sessionId: routedSession }
            : { kind: 'revalidate', sessionId: routedSession };
    }
    if (!slot.sessionId) return { kind: 'request' };
    if (routed && slot.source === 'routed') return { kind: 'request' };
    return { kind: 'book', sessionId: slot.sessionId };
}

/**
 * Whether a session's availability still offers the chosen time, and who would
 * host it. A round robin names every member free then; the first is who the
 * booking goes to, as the pooled picker does.
 */
export function findOfferedSlot(
    availability:
        | Pick<CalendarAvailabilityData, 'freeDaysByDuration'>
        | undefined,
    slot: Pick<BookingSlot, 'startTimestamp' | 'duration'>,
): { offered: boolean; organizerMemberId: number | null } {
    const group = availability?.freeDaysByDuration.find(
        (candidate) => candidate.duration === slot.duration,
    );
    for (const zone of group?.freeDays ?? []) {
        for (const day of zone.days) {
            const match = day.freeSlots.find(
                (free) => free.timestamp === slot.startTimestamp,
            );
            if (match) {
                return {
                    offered: true,
                    organizerMemberId:
                        match.availableMembers[0]?.memberId ?? null,
                };
            }
        }
    }
    return { offered: false, organizerMemberId: null };
}

/** How a booking link says where the meeting happens. */
export interface MeetingLocation {
    kind: 'online' | 'inPerson';
    /** One line for the reader, e.g. "In person at 12 Queen St". */
    label: string;
}

export function describeMeetingLocation(
    locationType: string | null | undefined,
    locationString: string | null | undefined,
): MeetingLocation {
    const place = locationString?.trim();
    if (locationType === 'in-person') {
        return {
            kind: 'inPerson',
            label: place ? `In person at ${place}` : 'In person',
        };
    }
    return { kind: 'online', label: 'Online video call' };
}

/** The "What we cover" lines of a booking link, one point per line. */
export const coverLinesOf = (cover: string | null | undefined): string[] =>
    (cover ?? '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, MAX_COVER_LINES);

/** The booking link details the gate presents, from whichever payload has them. */
export interface MeetingDetails {
    sessionName: string | null;
    location: MeetingLocation;
    customMessage: string | null;
    coverLines: string[];
    /** The link's own photo, when it has one. */
    imageUrl: string | null;
    meetingType: 'one-on-one' | 'random';
}

export function readMeetingDetails(
    availability:
        | Pick<
              CalendarAvailabilityData,
              | 'sessionName'
              | 'sessionTitle'
              | 'locationType'
              | 'locationString'
              | 'customMessage'
              | 'meetingCover'
              | 'meetingImage'
              | 'meetingType'
          >
        | undefined,
): MeetingDetails | null {
    if (!availability) return null;
    return {
        sessionName: availability.sessionName?.trim() || null,
        location: describeMeetingLocation(
            availability.locationType,
            availability.locationString,
        ),
        customMessage: availability.customMessage?.trim() || null,
        coverLines: coverLinesOf(availability.meetingCover),
        imageUrl:
            availability.meetingImage?.LargeAvatar ||
            availability.meetingImage?.Avatar ||
            null,
        meetingType:
            availability.meetingType === 'random' ? 'random' : 'one-on-one',
    };
}

/** The meeting as the time step names it: the link's name, or the stock phrase. */
export const meetingNameFor = (sessionName: string | null | undefined) => {
    const name = sessionName?.trim();
    return name ? `“${name}”` : 'a meeting';
};

/** The modal's heading: the booking link's name, else the button's own words. */
export const gateHeadingFor = (
    sessionName: string | null | undefined,
    ctaLabel: string | null | undefined,
): string => sessionName?.trim() || ctaLabel?.trim() || 'Book a Meeting';

/** The day and time as the success step and the enquiry body show it. */
export const formatSlotWhen = (
    slot: Pick<BookingSlot, 'startTimestamp' | 'timezoneName'>,
): string =>
    dayjs(slot.startTimestamp)
        .tz(slot.timezoneName)
        .format('dddd, D MMMM YYYY [at] h:mm A');

export type BookingContactErrors = Partial<
    Record<keyof BookingContact, string>
>;

const CONTACT_SCHEMA = z.object({
    firstName: FIRST_NAME_INPUT,
    lastName: LAST_NAME_INPUT,
    email: EMAIL_INPUT,
    phone: PHONE_NUMBER_INPUT,
    company: z.string().trim().max(100),
});

/** Every problem with the contact details at once. Empty when they can be sent. */
export function validateBookingContact(
    contact: BookingContact,
): BookingContactErrors {
    const result = CONTACT_SCHEMA.safeParse(contact);
    if (result.success) return {};
    const errors: BookingContactErrors = {};
    for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof BookingContact;
        errors[field] ??= issue.message;
    }
    return errors;
}

export const fullNameOf = (
    contact: Pick<BookingContact, 'firstName' | 'lastName'>,
) => `${contact.firstName} ${contact.lastName}`.trim();

/**
 * The enquiry body: the meeting the reader asked for, then anything they wrote
 * themselves, so the professional sees the time whichever way they read it.
 */
export function composeMeetingMessage({
    slot,
    itemTitle,
    company,
    note,
}: {
    slot: BookingSlot;
    itemTitle?: string;
    company?: string;
    note?: string;
}): string {
    const meeting = getMeetingRequestMessage({
        personName: slot.isCompany ? null : slot.subjectName,
        itemTitle,
        professional: null,
        companyName: company,
        startTimestamp: slot.startTimestamp,
        timezoneName: slot.timezoneName,
        durationMinutes: slot.duration,
    });
    const own = note?.trim();
    return own ? `${meeting}\n\n${own}` : meeting;
}

/**
 * The enquiry submit route's fields for a booking. Carries the form, the
 * answers and the branch when a form is involved; without one it is the
 * meeting request alone, which is how a guest gets an account made for them.
 */
export function buildBookingEnquiryFields({
    form,
    rules,
    answers,
    predicted,
    contact,
    slot,
    siteTreeId,
    professionalId,
    itemTitle,
    ctaSource,
    signedIn = false,
}: {
    form: PublicEnquiryForm | null;
    rules: ConfiguredEnquiryRules | null;
    answers: BookingFormAnswers;
    predicted: PredictedDestination | null;
    contact: BookingContact;
    slot: BookingSlot;
    siteTreeId: number;
    professionalId?: number;
    itemTitle?: string;
    /** The Book button's `Source`; a meeting request's server event carries it. */
    ctaSource?: string;
    /**
     * Only a signed-in member may send a book-meeting channel. A guest's enquiry goes out as
     * an ordinary one and becomes the meeting request once they are signed in.
     */
    signedIn?: boolean;
}): Record<string, string> {
    const formContext = buildFormContext(form, {
        declineHandling: 'inline',
        channel: signedIn ? meetingChannelFor(slot) : undefined,
    });
    const selection =
        form && rules
            ? buildConfiguredSelection(rules, answers, predicted)
            : undefined;
    const fields: Record<string, string | undefined> = {
        ...buildEnquiryFormPayload(formContext, selection),
        name: fullNameOf(contact),
        email: contact.email.trim(),
        phoneNumber: contact.phone.trim(),
        // Sharing needs a phone; the step requires one, so this holds.
        shareDetails: contact.phone.trim() ? 'true' : 'false',
        message: composeMeetingMessage({
            slot,
            itemTitle,
            company: contact.company,
            note: rules?.messageEnabled ? answers.message : undefined,
        }),
        siteTreeId: String(siteTreeId),
        targetProfessionalId: professionalId
            ? String(professionalId)
            : undefined,
        branchId: answers.branchId || undefined,
        suburbPostcode: answers.location?.value.region ?? undefined,
        locationResponse: answers.location
            ? JSON.stringify(answers.location.value)
            : undefined,
        [CTA_SOURCE_FIELD]: ctaSource,
    };
    return Object.fromEntries(
        Object.entries(fields).filter(
            (entry): entry is [string, string] =>
                entry[1] !== undefined && entry[1] !== '',
        ),
    );
}

/** The enquiry channel a meeting with this slot's subject is recorded under. */
export const meetingChannelFor = (
    slot: Pick<BookingSlot, 'teamMemberId'>,
): 'book_meeting_member' | 'book_meeting_company' =>
    slot.teamMemberId ? 'book_meeting_member' : 'book_meeting_company';

/**
 * The booking or request route's fields for the chosen time. Both carry the
 * page and the Book button the meeting came from, which its server events are
 * logged against.
 */
export function buildMeetingActionFields({
    follow,
    slot,
    contact,
    enquiryId,
    professionalUrlSegment,
    siteTreeId,
    itemTitle,
    organizerMemberId,
    ctaSource,
}: {
    follow: Exclude<MeetingFollowUp, { kind: 'revalidate' }>;
    slot: BookingSlot;
    contact: BookingContact;
    enquiryId: number | null;
    professionalUrlSegment: string;
    siteTreeId: number;
    itemTitle?: string;
    /** Who hosts a booked time; defaults to the one frozen on the slot. */
    organizerMemberId?: number | null;
    /** The Book button's `Source`. */
    ctaSource?: string;
}): Record<string, string> {
    const startTime = dayjs(slot.startTimestamp)
        .tz(slot.timezoneName)
        .format('YYYY-MM-DDTHH:mm:ssZ');
    const organizer = organizerMemberId ?? slot.organizerMemberId;
    const shared: Record<string, string | undefined> = {
        startTime,
        duration: String(slot.duration),
        timezoneName: slot.timezoneName,
        clientName: fullNameOf(contact),
        clientEmail: contact.email.trim(),
        clientPhone: contact.phone.trim(),
        clientCompany: contact.company.trim(),
        enquiryId: enquiryId ? String(enquiryId) : undefined,
        organizerMemberId: organizer ? String(organizer) : undefined,
        siteTreeId: String(siteTreeId),
        [CTA_SOURCE_FIELD]: ctaSource,
    };
    const fields: Record<string, string | undefined> =
        follow.kind === 'book'
            ? { ...shared, intent: 'book', sessionId: String(follow.sessionId) }
            : {
                  ...shared,
                  professionalUrlSegment,
                  itemTitle,
                  teamMemberId: slot.teamMemberId
                      ? String(slot.teamMemberId)
                      : undefined,
              };
    return Object.fromEntries(
        Object.entries(fields).filter(
            (entry): entry is [string, string] =>
                entry[1] !== undefined && entry[1] !== '',
        ),
    );
}

/** What the enquiry submit route said about a booking's enquiry. */
export type BookingEnquiryResult =
    | { kind: 'declined'; message: string }
    | { kind: 'authRequired'; publicUser: PublicUser }
    | {
          kind: 'created';
          enquiryId: number | null;
          routed: RoutedDestination | null;
          /** The submit made the reader an account and signed them in. */
          generatedMember: boolean;
      }
    | { kind: 'failed' };

interface SubmitResponseLike {
    success?: boolean;
    error?: boolean;
    declined?: boolean;
    declinedMessage?: string | null;
    publicUser?: PublicUser;
    data?: { __typename?: string; GeneratedMember?: unknown } | null;
}

export function readBookingEnquiryResult(data: unknown): BookingEnquiryResult {
    const shape =
        data && typeof data === 'object' ? (data as SubmitResponseLike) : null;
    if (!shape) return { kind: 'failed' };
    if (shape.error && shape.declined) {
        return {
            kind: 'declined',
            message: shape.declinedMessage?.trim() || DEFAULT_DECLINED_MESSAGE,
        };
    }
    if (shape.error && shape.publicUser) {
        return { kind: 'authRequired', publicUser: shape.publicUser };
    }
    if (shape.success && shape.data?.__typename === 'CreateEnquiryResult') {
        return {
            kind: 'created',
            enquiryId: readCreatedEnquiryId(data),
            routed: readSubmitOutcome(data).routedDestination,
            generatedMember: Boolean(shape.data.GeneratedMember),
        };
    }
    return { kind: 'failed' };
}

/** The Forms page type a CTA's page type names, as the item type the enquiry gate takes. */
export const itemTypeForPageType = (
    pageType: 'Product' | 'Professional' | 'Project' | 'Article' | undefined,
): 'product' | 'professional' | 'project' | 'article' => {
    switch (pageType) {
        case 'Product':
            return 'product';
        case 'Project':
            return 'project';
        case 'Article':
            return 'article';
        default:
            return 'professional';
    }
};

/** The booking gate's steps, in the order a reader meets them. */
export type BookMeetingGateStep =
    | 'details'
    | 'schedule'
    | 'contact'
    | 'sending'
    | 'success'
    | 'declined'
    | 'unavailable';

/**
 * Where an opening of the gate starts: on a send already under way (the far
 * side of a social sign-in), on the form's questions until they have been
 * answered, and otherwise on choosing a time.
 */
export function firstGateStep({
    asksDetails,
    detailsDone,
    sending,
}: {
    asksDetails: boolean;
    detailsDone: boolean;
    sending: boolean;
}): Extract<BookMeetingGateStep, 'details' | 'schedule' | 'sending'> {
    if (sending) return 'sending';
    if (asksDetails && !detailsDone) return 'details';
    return 'schedule';
}

/** First names read as a person talking; full names read as a record. */
export const firstNameOf = (fullName: string): string =>
    fullName.trim().split(/\s+/)[0] || fullName;

/** The "Your details" step's opening line: what, with whom, and when. */
export function describeChosenMeeting({
    slot,
    sessionName,
    professionalName,
    isBooked,
}: {
    slot: BookingSlot;
    sessionName: string | null | undefined;
    professionalName: string;
    isBooked: boolean;
}): string {
    const who = slot.isCompany
        ? `the team at ${professionalName}`
        : firstNameOf(slot.subjectName);
    const verb = isBooked ? 'Booking' : 'Requesting';
    return `${verb} ${meetingNameFor(sessionName)} with ${who} on ${formatSlotWhen(slot)}.`;
}
